import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import api from '../../api';

const GOLD = '#D4AF37';
const GOLD2 = '#f5c842';
const BG_CARD = '#0d1117';
const BORDER = 'rgba(255,255,255,0.07)';
const TEXT_SEC = '#6b7280';
const INPUT_BG = '#0a0f1e';

const STATUS_COLORS = { waiting: '#3b82f6', notified: GOLD, booked: '#10b981' };

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || '#6b7280';
  return (
    <span style={{
      background: c + '18', color: c, border: `1px solid ${c}30`,
      borderRadius: 20, padding: '3px 10px', fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>{status}</span>
  );
}

function InputField({ label, children }) {
  return (
    <div>
      <div style={{ color: TEXT_SEC, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', background: INPUT_BG, border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 10, padding: '12px', color: '#fff', fontSize: 14, outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit',
};

export default function Waitlist() {
  const [entries, setEntries] = useState([]);
  const [services, setServices] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ client_name: '', phone: '', email: '', service_id: '', preferred_date: '' });
  const [toast, setToast] = useState(null);
  const [notifying, setNotifying] = useState(null);

  useEffect(() => {
    load();
    api.get('/services').then(r => setServices(r.data)).catch(() => {});
  }, []);

  function load() {
    api.get('/waitlist').then(r => setEntries(r.data)).catch(() => {});
  }

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  async function addEntry(e) {
    e.preventDefault();
    try {
      await api.post('/waitlist', form);
      setForm({ client_name: '', phone: '', email: '', service_id: '', preferred_date: '' });
      setShowAdd(false);
      load();
      showToast('Added to waitlist ✓');
    } catch { showToast('Error adding to waitlist', 'error'); }
  }

  async function notify(id) {
    setNotifying(id);
    try {
      await api.post(`/waitlist/${id}/notify`);
      load();
      showToast('Client notified via SMS ✓');
    } catch { showToast('Error sending notification', 'error'); }
    finally { setNotifying(null); }
  }

  async function remove(id) {
    if (!window.confirm('Remove from waitlist?')) return;
    try {
      await api.delete(`/waitlist/${id}`);
      load();
      showToast('Removed');
    } catch { showToast('Error removing', 'error'); }
  }

  const waiting = entries.filter(e => e.status === 'waiting');
  const notified = entries.filter(e => e.status !== 'waiting');

  return (
    <AdminLayout>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 99999,
          background: toast.type === 'error' ? '#0d1117' : '#0d1117',
          border: `1px solid ${toast.type === 'error' ? '#ef4444' : GOLD}`,
          color: toast.type === 'error' ? '#ef4444' : GOLD,
          padding: '12px 20px', borderRadius: 12, fontWeight: 600, fontSize: 14,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}>{toast.msg}</div>
      )}

      <div style={{ maxWidth: 640 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <h1 className="page-title" style={{ margin: 0 }}>Waitlist</h1>
            <p style={{ color: TEXT_SEC, fontSize: 13, margin: '4px 0 0' }}>
              {waiting.length} waiting · {entries.length} total
            </p>
          </div>
          <button onClick={() => setShowAdd(v => !v)} style={{
            background: showAdd ? 'transparent' : `linear-gradient(135deg, ${GOLD}, ${GOLD2})`,
            color: showAdd ? TEXT_SEC : '#000',
            border: showAdd ? `1px solid ${BORDER}` : 'none',
            borderRadius: 12, padding: '12px 20px', fontWeight: 700, fontSize: 13,
            cursor: 'pointer', transition: 'all 0.2s',
          }}>
            {showAdd ? 'Cancel' : '+ Add to Waitlist'}
          </button>
        </div>

        {/* Add form */}
        {showAdd && (
          <div style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 24, marginBottom: 20, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${GOLD}60, ${GOLD})` }} />
            <h3 style={{ color: '#fff', fontSize: 15, fontWeight: 700, margin: '0 0 20px' }}>New Waitlist Entry</h3>
            <form onSubmit={addEntry}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <InputField label="Client Name *">
                  <input style={inputStyle} value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} required placeholder="John Smith" />
                </InputField>
                <InputField label="Phone">
                  <input style={inputStyle} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="(561) 555-0100" />
                </InputField>
                <InputField label="Email">
                  <input style={inputStyle} type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="john@example.com" />
                </InputField>
                <InputField label="Preferred Date">
                  <input style={inputStyle} type="date" value={form.preferred_date} onChange={e => setForm({...form, preferred_date: e.target.value})} />
                </InputField>
                <div style={{ gridColumn: '1/-1' }}>
                  <InputField label="Service">
                    <select style={{ ...inputStyle, appearance: 'none' }} value={form.service_id} onChange={e => setForm({...form, service_id: e.target.value})}>
                      <option value="">Any service</option>
                      {services.map(s => <option key={s.id} value={s.id}>{s.name} — ${s.price}</option>)}
                    </select>
                  </InputField>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button type="submit" style={{
                  background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`, color: '#000',
                  border: 'none', borderRadius: 10, padding: '12px 24px',
                  fontWeight: 800, fontSize: 13, cursor: 'pointer', flex: 1,
                }}>Add to Waitlist</button>
                <button type="button" onClick={() => setShowAdd(false)} style={{
                  background: 'transparent', color: TEXT_SEC, border: `1px solid ${BORDER}`,
                  borderRadius: 10, padding: '12px 18px', fontSize: 13, cursor: 'pointer',
                }}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Empty state */}
        {entries.length === 0 && (
          <div style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12, color: TEXT_SEC }}>—</div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Waitlist is empty</div>
            <div style={{ color: TEXT_SEC, fontSize: 13 }}>Clients will appear here when they join the waitlist.</div>
          </div>
        )}

        {/* Waiting */}
        {waiting.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: TEXT_SEC, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
              Waiting ({waiting.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {waiting.map(entry => (
                <div key={entry.id} style={{
                  background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 16,
                  padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14,
                }}>
                  {/* Avatar */}
                  <div style={{
                    width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                    background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, fontWeight: 800, color: '#3b82f6',
                  }}>
                    {entry.client_name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>{entry.client_name}</div>
                    <div style={{ color: TEXT_SEC, fontSize: 12, marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {entry.phone && <span>{entry.phone}</span>}
                      {entry.service_name && <span>· {entry.service_name}</span>}
                      {entry.preferred_date && (
                        <span>· {new Date(entry.preferred_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      )}
                    </div>
                  </div>
                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {entry.phone && (
                      <button
                        onClick={() => notify(entry.id)}
                        disabled={notifying === entry.id}
                        style={{
                          background: `${GOLD}15`, border: `1px solid ${GOLD}30`, color: GOLD,
                          borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700,
                          cursor: 'pointer', opacity: notifying === entry.id ? 0.6 : 1,
                        }}>
                        {notifying === entry.id ? '...' : 'Notify'}
                      </button>
                    )}
                    <button onClick={() => remove(entry.id)} style={{
                      background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                      color: '#ef4444', borderRadius: 8, padding: '8px 12px',
                      fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    }}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notified/Booked */}
        {notified.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: TEXT_SEC, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
              Already Notified ({notified.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {notified.map(entry => (
                <div key={entry.id} style={{
                  background: 'rgba(255,255,255,0.02)', border: `1px solid ${BORDER}`,
                  borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#9ca3af' }}>{entry.client_name}</div>
                    <div style={{ color: '#4b5563', fontSize: 11, marginTop: 1 }}>{entry.phone}</div>
                  </div>
                  <StatusBadge status={entry.status} />
                  <button onClick={() => remove(entry.id)} style={{
                    background: 'transparent', border: 'none', color: '#4b5563',
                    fontSize: 16, cursor: 'pointer', padding: '4px',
                  }}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
